import React from "react";

type InscreenObserverOptions = {
	once?: boolean;
	proximity?: number;
	threshold?: number;
	timeout?: number;
};

export function useInscreenObserver({
	callback,
	options = {},
}: {
	callback?: (target: Element) => void;
	options?: InscreenObserverOptions;
}): [(target: HTMLElement | null) => void] {
	const { once, proximity, threshold, timeout } = options;

	const observerRef = React.useRef<InscreenObserver | null>(null);

	React.useEffect(
		function initObserver() {
			if (callback) {
				observerRef.current = new InscreenObserver(callback, {
					proximity,
					threshold,
					timeout,
					once,
				});
			}
			return function disconnect() {
				observerRef.current?.disconnect();
				observerRef.current = null;
			};
		},
		[callback, once, proximity, threshold, timeout],
	);

	const [targetEle, setTargetEle] = React.useState<HTMLElement | null>(null);

	React.useEffect(
		function observeTarget() {
			if (targetEle !== null) {
				observerRef.current?.observe(targetEle);
			}
		},
		[targetEle],
	);

	const targetRef = React.useCallback((target: HTMLElement | null) => {
		setTargetEle((prevTarget) => {
			if (prevTarget !== null) {
				observerRef.current?.unobserve(prevTarget);
			}
			return target;
		});
	}, []);

	return [targetRef];
}

type Observable = {
	element: Element;
	timeout: number;
};

class InscreenObserver {
	private _callback: (target: Element) => void;
	private _once: boolean;
	private _threshold: number;
	private _timeout: number;
	private _observables: Observable[];
	private _observer: IntersectionObserver;

	constructor(
		callback: (target: Element) => void,
		{ once = false, proximity = 0, threshold = 0, timeout = 0 }: InscreenObserverOptions = {},
	) {
		this._callback = callback;
		this._once = once;
		this._threshold = threshold;
		this._timeout = timeout;
		this._observables = [];
		this._observer = new IntersectionObserver((entries) => this._handleObservations(entries), {
			rootMargin: `${proximity}px`,
			threshold,
		});
	}

	private _handleObservations(entries: IntersectionObserverEntry[]) {
		entries.forEach((entry) => {
			if (this._isEnteringViewport(entry)) {
				this._handleOrSetObservationTimeout(entry.target);
			} else {
				this._clearObservationTimeout(entry.target);
			}
		});
	}

	private _isEnteringViewport(entry: IntersectionObserverEntry) {
		return this._isInscreenByThreshold(entry);
	}

	private _isInscreenByThreshold(entry: IntersectionObserverEntry) {
		return entry.isIntersecting && entry.intersectionRatio >= this._threshold;
	}

	private _handleOrSetObservationTimeout(element: Element) {
		if (this._timeout > 0) {
			this._setObservationTimeout(element);
		} else {
			this._handleObservation(element);
		}
	}

	private _setObservationTimeout(element: Element) {
		const timeout = window.setTimeout(() => {
			this._handleObservation(element);
		}, this._timeout);
		this._addObservable(element, timeout);
	}

	private _handleObservation(element: Element) {
		this._callback(element);
		if (this._once) {
			this.unobserve(element);
		}
	}

	private _addObservable(element: Element, timeout: number) {
		this._observables.push({ element, timeout });
	}

	private _clearObservationTimeout(element: Element) {
		const observable = this._removeObservable(element);
		if (observable) {
			clearTimeout(observable.timeout);
		}
	}

	private _removeObservable(element: Element) {
		const isObservedElement = (observable: Observable) => observable.element === element;
		const observable = this._observables.find(isObservedElement);
		this._observables = this._observables.filter((obs) => obs !== observable);
		return observable;
	}

	observe(element: Element) {
		this._observer.observe(element);
	}

	unobserve(element: Element) {
		try {
			this._observer.unobserve(element);
		} catch (e) {
			console.log("InscreenObserver.unobserve: Failed to unobserve element.", e);
		}
		this._clearObservationTimeout(element);
	}

	disconnect() {
		this._observer.disconnect();
	}
}
